import moment from 'moment'

export function generateHexId(): string {
  const id = Math.floor(Math.random() * 4096)
  return id.toString(16).padStart(3, '0').toUpperCase()
}

export function responseDefaultForm(
  type: 'string' | 'number' | 'boolean',
): string | number | boolean {
  let response: any = ''

  switch (type) {
    case 'string':
      response = ''
      break
    case 'number':
      response = 0
      break
    case 'boolean':
      response = 'T'
      break
  }
  return response
}

export function cepTransform(value: number | string): string {
  const apenasDigitos = value.toString().replace(/\D/g, '')
  const cepNormalizado = apenasDigitos.padStart(8, '0')
  return cepNormalizado.replace(/^(\d{5})(\d{3})$/, '$1-$2')
}

export function cnpjTransform(value: string): string {
  if (!value) return value

  value = value.replace(/\D/g, '')
  if (value.length <= 2) return value
  if (value.length <= 5) return value.replace(/(\d{2})(\d{0,3})/, '$1.$2')
  if (value.length <= 8)
    return value.replace(/(\d{2})(\d{3})(\d{0,3})/, '$1.$2.$3')
  if (value.length <= 12)
    return value.replace(/(\d{2})(\d{3})(\d{3})(\d{0,4})/, '$1.$2.$3/$4')
  return value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
}

export function cpfTransform(value: string): string {
  if (!value) return value

  value = value.replace(/\D/g, '')
  if (value.length <= 3) return value
  if (value.length <= 6) return value.replace(/(\d{3})(\d{0,3})/, '$1.$2')
  if (value.length <= 9)
    return value.replace(/(\d{3})(\d{3})(\d{0,3})/, '$1.$2.$3')
  return value.replace(/(\d{3})(\d{3})(\d{3})(\d{1})/, '$1.$2.$3-$4')
}

export function phoneTransform(value: string): string {
  if (!value) return value

  value = value.replace(/\D/g, '') // Remove tudo o que não for número
  if (value.length <= 10) {
    return value.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3') // Formato (XX) XXXX-XXXX
  } else if (value.length === 11) {
    return value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3') // Formato (XX) XXXXX-XXXX
  }
  return value
}

export function removeAccents(string: string): string {
  return string.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

export function removeAccentsAndSpacesAndLowercase(string: string): string {
  return string
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s/g, '')
    .toLowerCase()
}

// Espelha NormalizarTextoHelper.NormalizarTexto do backend:
// lowercase + trim + remove acentos, PRESERVANDO espa\u00e7os internos.
// Usado para casar a chave de unicidade de paradas (cidade+estado) com o backend.
export function normalizarTexto(texto: string | null | undefined): string {
  if (!texto) return ''
  return texto
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .normalize('NFC')
}

export function validTreatList(response: any): boolean {
  if (response && response.statusCode == 200 && response.data.length > 0) {
    return true
  } else {
    return false
  }
}

export function convertCurrency(value: any): string | number {
  let currency: any = ''
  if (typeof value == 'string') {
    currency = parseFloat(value.replaceAll('.', '').replaceAll(',', '.'))
  } else if (typeof value == 'number') {
    currency = value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  return currency
}

// Monta a linha de endereço de um elemento do itinerário no formato:
// "{localParada} - {logradouro}, {numero} - {bairro}, {cidade} - {estado}"
// Partes vazias são omitidas; localParada "Origem"/"Destino" não vira prefixo.
export function formatItineraryAddress(el: any): string {
  if (!el) return ''

  const nome =
    el.localParada && el.localParada !== 'Origem' && el.localParada !== 'Destino'
      ? el.localParada
      : ''
  const rua = [el.logradouro, el.numero].filter(Boolean).join(', ')
  const cidadeEstado =
    el.cidade && el.estado
      ? `${el.cidade} - ${el.estado}`
      : el.cidade || el.estado || ''
  const regiao = [el.bairro, cidadeEstado].filter(Boolean).join(', ')

  return [nome, rua, regiao].filter(Boolean).join(' - ')
}

// Monta um bloco de mensagem (estilo WhatsApp) de uma solicitação para envio manual
// ao fornecedor. `itinerary` é a lista achatada [origem?, ...paradas, destino?].
// `pax` é a quantidade de passageiros da proposta; a linha só entra quando informada.
export function buildSupplierQuoteMessage(
  item: any,
  itinerary: any[],
  pax?: number | null,
): string {
  const enderecos = (itinerary ?? [])
    .map((el) => formatItineraryAddress(el))
    .filter(Boolean)
    .join('\nx\n')

  return [
    `*${item?.codigoTg ?? ''}*`,
    `*Data: ${item?.dataInicio ?? ''}*`,
    '',
    `${item?.tipoVeiculo ?? ''} - ${item?.tipoMotorista ?? ''}`,
    `*${item?.tipoServico ?? ''}* - *${item?.servico ?? ''}*`,
    ...(pax ? [`*PAX: ${pax}*`] : []),
    '',
    '*DESCRIÇÃO*',
    '',
    enderecos,
    '',
    '',
    '*VALOR:*',
  ].join('\n')
}

export function formatDateForm(dateValue: any): string {
  if (!dateValue) {
    return ''
  }

  const momentDate = moment(dateValue)

  if (momentDate.isValid()) {
    return momentDate.format('DD/MM/YYYY')
  }

  return ''
}

// "27:30:00" -> 99000. As horas podem passar de 24: é duração, não relógio.
// Valor ausente ou malformado vira 0, para um registro sujo não derrubar uma soma.
export function durationToSeconds(duration: string | null | undefined): number {
  if (!duration) {
    return 0
  }

  const parts = duration.split(':').map((part) => Number(part))
  if (parts.length != 3 || parts.some((part) => !Number.isFinite(part))) {
    return 0
  }

  return parts[0] * 3600 + parts[1] * 60 + parts[2]
}

// 99000 -> "27:30:00", mesmo formato que o back devolve no tempo útil do histórico
export function secondsToDuration(seconds: number): string {
  const total = Number.isFinite(seconds) && seconds > 0 ? Math.round(seconds) : 0
  const horas = Math.floor(total / 3600)
  const minutos = Math.floor((total % 3600) / 60)
  const restante = total % 60

  return [horas, minutos, restante]
    .map((part) => `${part}`.padStart(2, '0'))
    .join(':')
}
